import { zodResolver } from "@hookform/resolvers/zod";
import { MouseEventHandler, useEffect, useMemo, useState } from "react";
import {
  Control,
  Resolver,
  useForm,
  UseFormRegister,
  UseFormResetField,
} from "react-hook-form";
import { fullName } from "tng-address-collection-shared/fullName.ts";
import {
  CreateCardRequest,
  EditCardRequest,
  SenderId,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { fallbackCard } from "../defaults/fallbackCard.tsx";
import { useConfirmCardContactDetails } from "../hooks/useConfirmCardContactDetails.ts";
import { useDeleteCard } from "../hooks/useDeleteCard.ts";
import { useEditCard } from "../hooks/useEditCard.ts";
import { useEligibleSenderIds } from "../hooks/useEligibleSenderIds.ts";
import { useGetCardEditView } from "../hooks/useGetCardEditView.ts";
import { useTranslator } from "../i18n/translator.ts";
import { CardFormFields, CardNameRef } from "./CardFormFields.tsx";
import { ConfirmCardContactDetailsDialog } from "./ConfirmCardContactDetailsDialog.tsx";
import { ContactDetailsConsistencyAlert } from "./ContactDetailsConsistencyAlert.tsx";
import { FormContainer } from "./FormContainer.tsx";

const editCardRequestWithSenderAmong = (eligibleSenderIds: SenderId[]) => {
  const eligibleUidNumbers = new Set(
    eligibleSenderIds.flatMap((eligible) =>
      eligible.type === "employee" ? [eligible.uidNumber] : [],
    ),
  );
  return EditCardRequest.refine(
    ({ senderId }) =>
      senderId?.type !== "employee" ||
      eligibleUidNumbers.has(senderId.uidNumber),
    {
      params: { translationKey: "cardErrorSenderUnknownHint" },
      path: ["senderId"],
    },
  );
};

interface EditCardDialogProps {
  cardId: string | undefined;
  projectId: string;
  onClose: () => void;
  cards: CardNameRef[];
  canDelete: boolean;
}

export const EditCardDialog = ({
  cardId,
  projectId,
  onClose,
  canDelete,
  cards,
}: EditCardDialogProps) => {
  const translate = useTranslator();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const { data: cardData, isPlaceholderData } = useGetCardEditView(cardId, {
    projectId,
  });

  const { mutate: editCard, isPending: isEditPending } = useEditCard();

  const {
    data: eligibleSenderIds,
    isPlaceholderData: isEligibleSenderIdsPlaceholder,
  } = useEligibleSenderIds();
  const schema = useMemo(
    () =>
      eligibleSenderIds && !isEligibleSenderIdsPlaceholder
        ? editCardRequestWithSenderAmong(eligibleSenderIds)
        : EditCardRequest,
    [eligibleSenderIds, isEligibleSenderIdsPlaceholder],
  );

  const { control, handleSubmit, reset, resetField, register, formState } =
    useForm({
      resolver: zodResolver(schema) as Resolver<EditCardRequest>,
      defaultValues: cardData ?? fallbackCard,
      disabled: isPlaceholderData,
    });

  const { mutate: deleteCard, isPending: isDeletePending } =
    useDeleteCard(projectId);
  const { mutate: confirmCardContactDetails, isPending: isConfirmPending } =
    useConfirmCardContactDetails({
      cardId: cardId ?? "",
      projectId,
    });

  useEffect(() => {
    reset(cardData, { keepDirtyValues: true });
  }, [cardData, reset]);

  const showContactDetailsWarning = cardData?.hasContactDetailsWarning === true;

  const onSubmit = (formData: EditCardRequest) => {
    editCard(formData, {
      onSuccess: onClose,
    });
  };

  const openConfirmDialog = () => {
    setIsConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  const confirmContactDetails = () => {
    confirmCardContactDetails(undefined, {
      onSuccess: closeConfirmDialog,
    });
  };

  const deleteConfig =
    !canDelete || !cardData
      ? undefined
      : {
          confirmationMessage: [
            cardData.isSharedByOtherProjects &&
              translate("deleteConfirmationMessageWithSharedCards"),
            translate("deleteConfirmationMessage", {
              recipient: fullName(cardData.recipient),
            }),
          ]
            .filter(Boolean)
            .join("\n"),
          onDelete: () => {
            deleteCard(cardData.id, {
              onSuccess: onClose,
              onError: onClose,
            });
          },
          isDeleteDisabled: isDeletePending,
        };

  return (
    <FormContainer
      title={translate("editCardTitle")}
      onClose={onClose}
      isLoading={!cardData}
      saveConfig={{
        onSave: handleSubmit(onSubmit) as MouseEventHandler,
        isSaveDisabled:
          isEditPending || isPlaceholderData || !formState.isDirty,
      }}
      deleteConfig={deleteConfig}
    >
      {showContactDetailsWarning && (
        <ContactDetailsConsistencyAlert
          warning={cardData?.contactDetailsWarning}
          isPlaceholderData={isPlaceholderData}
          isConfirmPending={isConfirmPending}
          onConfirm={openConfirmDialog}
        />
      )}
      {showContactDetailsWarning && (
        <ConfirmCardContactDetailsDialog
          open={isConfirmDialogOpen}
          isPending={isConfirmPending}
          onClose={closeConfirmDialog}
          onConfirm={confirmContactDetails}
        />
      )}
      <CardFormFields
        control={control as Control<CreateCardRequest | EditCardRequest>}
        resetField={
          resetField as UseFormResetField<CreateCardRequest | EditCardRequest>
        }
        register={
          register as UseFormRegister<CreateCardRequest | EditCardRequest>
        }
        projectId={projectId}
        cards={cards}
        currCardId={cardData?.id}
        emailDuplicateState={{ status: "unique" }}
      />
    </FormContainer>
  );
};
